import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getNote, mapApiNoteToUi } from '../../../services/notes.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * One note, with its body and links, for the preview slide-out (CT-5528).
 *
 * 403 and 404 stay separate, like everywhere in this domain: a note the user may
 * not read renders access-denied, never "not found". `noteId` of `null` is idle,
 * which is what a closed slide-out passes. `fetchIdRef` drops a stale response
 * when the user opens another note before the first one answers.
 */
export function useNote({ noteId } = {}) {
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(Boolean(noteId));
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchNote = useCallback(async () => {
    if (!noteId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const { body } = await getNote({ noteId });
      if (requestId !== fetchIdRef.current) return;
      setNote(mapApiNoteToUi(body));
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setNote(null);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load the note');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [noteId]);

  useEffect(() => {
    fetchNote();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchNote]);

  return useMemo(
    () => ({
      note,
      loading,
      error,
      accessDenied: status === 403,
      notFound: status === 404,
      refetch: fetchNote,
    }),
    [note, loading, error, status, fetchNote],
  );
}

export default useNote;
